import { useEffect, useState } from "react";
import client from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ScrollText, Link2, Search } from "lucide-react";

// "attendance.manual_add" -> "Attendance · Manual add"
const actionLabel = (a = "") => {
  const [area, ...rest] = a.split(".");
  const words = (s) => s.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
  return rest.length ? `${words(area)} · ${words(rest.join(" "))}` : words(area);
};

export default function Audit() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [userId, setUserId] = useState("all");
  const [people, setPeople] = useState([]);
  const { t } = useI18n();

  useEffect(() => {
    client.get("/users").then((r) => setPeople(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const h = setTimeout(() => {
      setLoading(true);
      const params = { limit: 1000, q };
      if (start) params.start = start;
      if (end) params.end = end;
      if (userId !== "all") params.userId = userId;
      client.get("/audit", { params }).then((r) => setEvents(r.data)).catch(() => {}).finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(h);
  }, [q, start, end, userId]);
  const filtered = q || start || end || userId !== "all";

  return (
    <div className="space-y-6" data-testid="audit-page">
      <div>
        <h2 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2">
          <ScrollText className="h-6 w-6 text-slate-400" /> {t("nav.audit", "Audit Trail")}
        </h2>
        <p className="text-sm text-slate-500 flex items-center gap-1.5">
          <Link2 className="h-3.5 w-3.5" /> {t("audit.subtitle", "Hash-chained, tamper-evident. Newest first.")}
        </p>
      </div>
      <div className="flex flex-wrap items-end gap-3" data-testid="audit-filters">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by cleaner, site, action or person…" className="pl-8" data-testid="audit-search" />
        </div>
        <div className="w-full sm:w-60">
          <p className="text-xs text-slate-500 mb-1">Person</p>
          <Select value={userId} onValueChange={setUserId}>
            <SelectTrigger data-testid="audit-person-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Everyone</SelectItem>
              {people.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`audit-person-${p.id}`}>{p.displayName}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <p className="text-xs text-slate-500 mb-1">From</p>
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-40" data-testid="audit-from" />
        </div>
        <div>
          <p className="text-xs text-slate-500 mb-1">To</p>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-40" data-testid="audit-to" />
        </div>
        {filtered && (
          <Button variant="ghost" onClick={() => { setQ(""); setStart(""); setEnd(""); setUserId("all"); }} data-testid="audit-clear-filters">Clear</Button>
        )}
        <p className="text-xs text-slate-400 ml-auto" data-testid="audit-count">{loading ? "Loading…" : `${events.length} entries`}</p>
      </div>

      <Card className="border-slate-200 overflow-x-auto">
        {loading && !events.length ? <p className="p-8 text-slate-500 text-sm">Loading…</p> : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("audit.time", "Time")}</TableHead><TableHead>Done by</TableHead><TableHead>{t("audit.action", "Action")}</TableHead>
                <TableHead className="min-w-[280px]">Details</TableHead><TableHead>{t("common.reason", "Reason")}</TableHead><TableHead>{t("audit.hash", "Hash")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.map((e) => (
                <TableRow key={e.hash} data-testid={`audit-row-${e.seq || e.hash}`} className="hover:bg-slate-50 align-top">
                  <TableCell className="text-xs text-slate-500 whitespace-nowrap">{new Date(e.timestamp).toLocaleString()}</TableCell>
                  <TableCell className="text-sm"><p className="font-medium text-slate-800">{e.actorName}</p>{e.actorName !== e.actor && <p className="text-[11px] text-slate-400">{e.actor}</p>}</TableCell>
                  <TableCell><Badge variant="outline" className="text-[11px] bg-slate-50 whitespace-nowrap" title={e.action}>{actionLabel(e.action)}</Badge></TableCell>
                  <TableCell className="text-sm" data-testid={`audit-details-${e.seq}`}>
                    {e.details && <p className="font-medium text-slate-800">{e.details}</p>}
                    {(e.changes || []).map((c, i) => <p key={i} className="text-xs text-slate-600">{c}</p>)}
                    {!e.details && !(e.changes || []).length && <span className="text-slate-400">{e.entity}</span>}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500 max-w-[220px] whitespace-pre-wrap">{e.reason || "—"}</TableCell>
                  <TableCell className="font-mono text-[10px] text-slate-400 truncate max-w-[120px]">{e.hash?.slice(0, 12)}…</TableCell>
                </TableRow>
              ))}
              {!loading && !events.length && <TableRow><TableCell colSpan={6} className="text-center text-sm text-slate-400 py-8">No matching entries.</TableCell></TableRow>}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
